import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Loader2, Save, CreditCard, ShieldCheck, FileText, ArrowRight,
  Clock, Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';
import { thesisApi } from '@/api/thesisApi';
import { useSubscription } from '@/hooks/useSubscription';
import InkPhDLogo from '@/components/InkPhDLogo';
import PlanBadge from '@/components/PlanBadge';
import UserMenu from '@/components/UserMenu';
import UsageBreakdownCard from '@/components/UsageBreakdownCard';
import AuthorIdeasPanel from '@/components/AuthorIdeasPanel';
import ReviewPanel from '@/components/ReviewPanel';
import AppFooter from '@/components/AppFooter';
import SeoHead from '@/components/SeoHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ['PhD candidate', 'Postdoctoral researcher', 'Clinical investigator',
  'Faculty', 'Systematic reviewer', 'Medical writer', 'Other'];

function Meter({ used, limit }) {
  if (limit < 0) return <p className="text-[12px] text-muted-foreground">Unlimited</p>;
  const pct = limit === 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const tone = pct >= 100 ? 'bg-rose-500' : pct >= 75 ? 'bg-amber-500' : 'bg-[#1d4ed8]';
  return (
    <div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">{used} of {limit} used</p>
    </div>
  );
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(authStorage.getAuthorUser());
  const { sub, loading: subLoading } = useSubscription();
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', town: '', country: '', occupation: '', researcher_role: '',
  });

  useEffect(() => {
    if (!authStorage.isAuthorAuthenticated()) { navigate('/thesis/login'); return; }
    const u = authStorage.getAuthorUser();
    setUser(u);
    setForm({
      first_name: u.first_name || '', last_name: u.last_name || '',
      town: u.town || '', country: u.country || '',
      occupation: u.occupation || '', researcher_role: u.researcher_role || 'PhD candidate',
    });
  }, [navigate]);

  const loadProjects = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await thesisApi.listProjects(user.id);
      setProjects(data.projects || []);
    } catch { /* dashboard already surfaces load errors */ }
  }, [user?.id]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/auth/profile`, form, { params: { user_id: user.id } });
      authStorage.updateAuthorUser({ ...user, ...data });
      setUser({ ...user, ...data });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const trialing = sub?.state === 'trialing';
  const limits = sub?.limits || {};
  const usage = sub?.usage || {};

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <SeoHead title="Your profile" path="/thesis/profile" noindex
        description="Your InkPhD account, plan and research projects." />

      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/thesis" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="profile-back-link">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Projects</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <InkPhDLogo size="sm" />
            <PlanBadge compact />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-3 sm:px-6 py-8 sm:py-12 space-y-6"
        data-testid="profile-page">
        <div>
          <h1 className="text-4xl sm:text-5xl font-serif font-semibold tracking-tight mb-2"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            Your <span className="text-[#1d4ed8] dark:text-[#7aa9ff]">account</span>
          </h1>
          <p className="text-muted-foreground text-base">
            Signed in as {user.email} · member since {String(user.created_at || '').slice(0, 10) || '—'}
          </p>
        </div>

        {/* Subscription */}
        <Card data-testid="profile-subscription-card">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#1d4ed8]" />
                  {subLoading ? 'Loading plan…' : `Plan: ${sub?.plan_name || 'Starter'}`}
                </CardTitle>
                <CardDescription className="mt-1">
                  {trialing ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300 font-medium">
                      <Clock className="w-3.5 h-3.5" />
                      Free trial · {sub.trial?.days_left} days left
                      {sub.trial?.ends_at && ` (until ${String(sub.trial.ends_at).slice(0, 10)})`}
                    </span>
                  ) : (
                    <>Status {sub?.state || 'free'}
                      {sub?.next_payment_at ? ` · renews ${String(sub.next_payment_at).slice(0, 10)}` : ''}</>
                  )}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button asChild size="sm" className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full"
                  data-testid="profile-manage-plan-btn">
                  <Link to="/thesis/billing">Manage plan</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="rounded-full"
                  data-testid="profile-invoices-link">
                  <Link to="/legal/billing"><Receipt className="w-3.5 h-3.5 mr-1.5" />Invoices</Link>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {trialing && (
              <div className="mb-4 p-3 rounded-lg border border-amber-500/40 bg-amber-500/[0.06] text-[12px]">
                Your trial unlocks the full <strong>Doctoral</strong> feature set. No card was
                required and nothing is charged automatically — when the trial ends the account
                simply moves to the free Starter plan and all your work stays in place.
              </div>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div data-testid="profile-usage-generations">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                  AI generations / month
                </p>
                <Meter used={usage.generations_per_month || 0} limit={limits.generations_per_month ?? -1} />
              </div>
              <div data-testid="profile-usage-projects">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                  Inline AI assists
                </p>
                <Meter used={usage.assists_per_month || 0} limit={limits.assists_per_month ?? -1} />
              </div>
              <div data-testid="profile-usage-projects-count">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                  Projects
                </p>
                <Meter used={usage.projects || 0} limit={limits.projects ?? -1} />
              </div>
              <div data-testid="profile-usage-documents">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                  Sources
                </p>
                <Meter used={usage.documents || 0} limit={limits.documents ?? -1} />
              </div>
              <div data-testid="profile-usage-exports">
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                  Export formats
                </p>
                <div className="flex flex-wrap gap-1">
                  {(limits.exports || []).map((f) => (
                    <Badge key={f} variant="outline" className="text-[10px] uppercase">{f}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account details */}
        <UsageBreakdownCard />

        <AuthorIdeasPanel userId={user.id} />

        <ReviewPanel userId={user.id} />

        <Card data-testid="profile-details-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your details</CardTitle>
            <CardDescription>
              These appear on exports and title pages. Your email and username cannot be changed here.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fn">First name</Label>
                <Input id="fn" value={form.first_name} onChange={set('first_name')} data-testid="profile-firstname" />
              </div>
              <div>
                <Label htmlFor="ln">Last name</Label>
                <Input id="ln" value={form.last_name} onChange={set('last_name')} data-testid="profile-lastname" />
              </div>
              <div>
                <Label htmlFor="town">City / Town</Label>
                <Input id="town" value={form.town} onChange={set('town')} data-testid="profile-town" />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input id="country" value={form.country} onChange={set('country')} data-testid="profile-country" />
              </div>
              <div>
                <Label htmlFor="occ">Occupation</Label>
                <Input id="occ" value={form.occupation} onChange={set('occupation')} data-testid="profile-occupation" />
              </div>
              <div>
                <Label htmlFor="role">Researcher role</Label>
                <select id="role" value={form.researcher_role} onChange={set('researcher_role')}
                  data-testid="profile-role"
                  className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {ROLES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2 flex items-center gap-3 pt-1">
                <Button type="submit" disabled={saving}
                  className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white rounded-full"
                  data-testid="profile-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save changes
                </Button>
                <Link to="/thesis/account" className="text-[12px] underline text-muted-foreground hover:text-foreground"
                  data-testid="profile-data-link">
                  Export or erase my data
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Projects */}
        <Card data-testid="profile-projects-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#1d4ed8]" /> Your projects ({projects.length})
                </CardTitle>
                <CardDescription>Everything you have started, most recent first.</CardDescription>
              </div>
              <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="profile-all-projects-btn">
                <Link to="/thesis">Open dashboard <ArrowRight className="w-3 h-3 ml-1" /></Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="profile-projects-empty">
                No projects yet. <Link to="/thesis" className="underline">Start your first one</Link>.
              </p>
            ) : (
              <ul className="divide-y divide-border" data-testid="profile-projects-list">
                {projects.map((p) => (
                  <li key={p.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/thesis/${p.id}`} className="text-sm font-medium hover:underline block break-words"
                        data-testid={`profile-project-${p.id}`}>
                        {p.title}
                      </Link>
                      <p className="text-[11px] text-muted-foreground">
                        {(p.project_type || 'research').replace(/_/g, ' ')} · {p.document_count || 0} sources ·{' '}
                        {p.chapter_count || 0} sections
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {(p.language || 'en').toUpperCase()}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card data-testid="profile-privacy-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> Data &amp; privacy
            </CardTitle>
          </CardHeader>
          <CardContent className="text-[13px] space-y-1 text-foreground/85">
            <p>· We never train models on your manuscripts or evidence library.</p>
            <p>
              · <Link to="/thesis/account" className="underline">Export or erase everything</Link> ·{' '}
              <Link to="/legal/privacy" className="underline">Privacy</Link> ·{' '}
              <Link to="/legal/billing" className="underline">Billing terms</Link>
            </p>
          </CardContent>
        </Card>
      </main>

      <AppFooter lang="en" />
    </div>
  );
}
